import { enhancedTmsApiService } from '#api/enhancements/enhancedTmsApiService'
import type {
  EnhancedConfigTypeId,
  EnhancedLanguageId,
  EnhancedMarketId,
  EnhancedVehicleTypeId,
  EnhancedViewId,
} from '#api/enhancements/enhancedTmsApiTypes'
import { store } from '#app/store'
import type { EnhancedLanguage, EnhancedMarket } from '#types/EnhancedMarket'

interface MappingResult {
  market?: EnhancedMarket
  language?: EnhancedLanguage
  countryCode?: string
  languageCode?: string
  marketId?: EnhancedMarketId
  languageId?: EnhancedLanguageId
  viewId?: EnhancedViewId
  vehicleTypeId?: EnhancedVehicleTypeId
  configurationTypeId?: EnhancedConfigTypeId
}

async function loadMarkets(): Promise<EnhancedMarket[]> {
  const request = store.dispatch(
    enhancedTmsApiService.endpoints.getAdmintoolMarkets.initiate({
      recursive: false,
    }),
  )
  const result = await request.unwrap()
  return result.markets as EnhancedMarket[]
}

export const lookupMarketAndLanguageMapping = async (
  countryCodeOrMarketId: string | number | undefined,
  languageCodeOrLanguageId: string | number | undefined,
): Promise<MappingResult> => {
  if (countryCodeOrMarketId === undefined) {
    return {}
  }

  const markets = await loadMarkets()
  const market = markets.find((market) =>
    typeof countryCodeOrMarketId === 'number'
      ? market.marketId === countryCodeOrMarketId
      : market.countryCode.toLowerCase() === countryCodeOrMarketId.toLowerCase(),
  )

  if (!market) {
    return {}
  }

  if (languageCodeOrLanguageId === undefined) {
    return {
      market,
      language: market.languages?.[0],
      countryCode: market.countryCode,
      languageCode: market.languages?.[0]?.languageCode,
      marketId: market.marketId,
      languageId: market.languages?.[0]?.languageId,
      viewId: market.viewId,
      vehicleTypeId: market.vehicleTypeId,
      configurationTypeId: market.configTypeId || undefined,
    }
  }

  const languageMapping = market.languages?.find((languageMapping) =>
    typeof languageCodeOrLanguageId === 'number'
      ? languageMapping.languageId === languageCodeOrLanguageId
      : languageMapping.languageCode.toLowerCase() === languageCodeOrLanguageId.toLowerCase(),
  )

  if (!languageMapping) {
    return {
      countryCode: market.countryCode,
      languageCode: market.languages?.[0]?.languageCode,
      marketId: market.marketId,
      languageId: market.languages?.[0]?.languageId,
      viewId: market.viewId,
      vehicleTypeId: market.vehicleTypeId,
      configurationTypeId: market.configTypeId || undefined,
    }
  }

  return {
    countryCode: market.countryCode,
    languageCode: languageMapping.languageCode,
    marketId: market.marketId,
    languageId: languageMapping.languageId,
    viewId: market.viewId,
    vehicleTypeId: market.vehicleTypeId,
    configurationTypeId: market.configTypeId || undefined,
  }
}
